import { TIPOS_DE_CAMPO_FILTRAVEIS, type ConexaoDoKommo } from "@base/contracts";
import { Button, cn } from "@base/ui";
import { Check, CheckCircle2, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { useDefinirCamposDoFiltro } from "../application/conexoes.contexto";

const ROTULO_DO_TIPO: Readonly<Record<string, string>> = {
  select: "Lista",
  multiselect: "Múltipla escolha",
  radiobutton: "Opção única",
  category: "Categoria",
  tracking_data: "Rastreio (UTM)",
  text: "Texto",
};

/**
 * Quais campos personalizados de lead do Kommo aparecem nos filtros do
 * Dashboard. So os escolhidos sao copiados dos leads; salvar rele os leads.
 */
export function CamposDoFiltro({
  clienteId,
  conexao,
}: {
  readonly clienteId: string;
  readonly conexao: ConexaoDoKommo;
}) {
  const [escolhidos, setEscolhidos] = useState<ReadonlySet<number>>(() => new Set(conexao.camposDoFiltro));
  const salvar = useDefinirCamposDoFiltro(clienteId);
  const alterado =
    escolhidos.size !== conexao.camposDoFiltro.length || conexao.camposDoFiltro.some((id) => !escolhidos.has(id));

  function alternar(id: number) {
    salvar.reset();
    setEscolhidos((atuais) => {
      const proximos = new Set(atuais);
      if (proximos.has(id)) proximos.delete(id);
      else proximos.add(id);
      return proximos;
    });
  }

  return (
    <section aria-labelledby="titulo-campos-do-filtro" className="flex flex-col gap-bloco">
      <div className="flex flex-col gap-1">
        <h2 id="titulo-campos-do-filtro" className="text-xl font-normal tracking-tight">
          Filtros do Dashboard
        </h2>
        <p className="text-sm text-muted-foreground">
          Escolha os campos personalizados do Kommo que aparecem no botão Filtros do Dashboard, como Origem e Sub
          Origem.
        </p>
      </div>

      <div className="overflow-hidden rounded-3xl bg-card shadow-sm ring-1 ring-border/70">
        {conexao.camposPersonalizados.filter((campo) => (TIPOS_DE_CAMPO_FILTRAVEIS as readonly string[]).includes(campo.tipo)).length === 0 ? (
          <p className="px-6 py-8 text-sm text-muted-foreground">
            Nenhum campo personalizado de lead encontrado nesta conta do Kommo. Eles aparecem aqui depois da próxima
            sincronização.
          </p>
        ) : (
          <ul className="grid gap-2 p-4 sm:grid-cols-2 xl:grid-cols-3">
            {/* UTMs por ultimo: Origem e afins sao os mais procurados. */}
            {[...conexao.camposPersonalizados]
              .filter((campo) => (TIPOS_DE_CAMPO_FILTRAVEIS as readonly string[]).includes(campo.tipo))
              .sort((a, b) => Number(a.tipo === "tracking_data") - Number(b.tipo === "tracking_data"))
              .map((campo) => {
              const marcado = escolhidos.has(campo.id);
              return (
                <li key={campo.id}>
                  <button
                    type="button"
                    aria-pressed={marcado}
                    onClick={() => alternar(campo.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors",
                      marcado ? "border-primary bg-primary/5" : "border-border hover:bg-accent",
                    )}
                  >
                    <span
                      className={cn(
                        "grid size-5 shrink-0 place-items-center rounded-md border",
                        marcado ? "border-primary bg-primary text-primary-foreground" : "border-input",
                      )}
                      aria-hidden="true"
                    >
                      {marcado ? <Check className="size-3.5" /> : null}
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium">{campo.nome}</span>
                      <span className="text-xs text-muted-foreground">
                        {ROTULO_DO_TIPO[campo.tipo] ?? campo.tipo}
                        {campo.opcoes.length > 0 ? ` · ${campo.opcoes.length} opções` : ""}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex flex-col gap-3 border-t bg-muted/30 px-6 py-4 md:flex-row md:items-center md:justify-between">
          <p
            role={salvar.isError ? "alert" : "status"}
            className={cn(
              "flex items-center gap-2 text-sm",
              salvar.isError ? "text-destructive" : salvar.isSuccess ? "text-success" : "text-muted-foreground",
            )}
          >
            {salvar.isSuccess ? <CheckCircle2 className="size-4" aria-hidden="true" /> : null}
            {salvar.isError
              ? mensagemDoErro(salvar.error)
              : salvar.isSuccess
                ? "Campos salvos. Os leads estão sendo lidos de novo do Kommo."
                : `${escolhidos.size} de ${conexao.camposPersonalizados.length} campos no filtro. Campos de texto livre podem ter dados pessoais: marque só o necessário.`}
          </p>
          <Button
            disabled={!alterado || salvar.isPending || escolhidos.size > 20}
            onClick={() => salvar.mutate([...escolhidos])}
          >
            {salvar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
            Salvar filtros
          </Button>
        </div>
      </div>
    </section>
  );
}
